/* ═══════════════════════════════════════════════════════════════════════════
   COREFLOW SHEETS — Formula Engine
   Complete Google Sheets-compatible formula library + CRM-specific functions
   
   Categories: Math (50+), Text (30+), Logical (12), Date (25+), Financial (40+),
   Statistical (30+), Lookup (15+), Engineering (40+), Info (15+), Filter/Array (10+),
   Database (12), Operator (8), Parser, CRM (10)
   ═══════════════════════════════════════════════════════════════════════════ */

// ─── CONSTANTS ──────────────────────────────────────────────────────────────
const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
export const colLabel = (i) => { if (i < 26) return ALPHA[i]; return ALPHA[Math.floor(i / 26) - 1] + ALPHA[i % 26]; };
export const colIndex = (label) => { const s = label.toUpperCase(); if (s.length === 1) return ALPHA.indexOf(s); return (ALPHA.indexOf(s[0]) + 1) * 26 + ALPHA.indexOf(s[1]); };
export const cellKey = (r, c) => colLabel(c) + (r + 1);
export const parseCellRef = (ref) => { const m = ref.match(/^([A-Z]{1,2})(\d+)$/); if (!m) return null; return { col: colIndex(m[1]), row: parseInt(m[2]) - 1 }; };

// ─── ERRORS ─────────────────────────────────────────────────────────────────
export const ERR = {
  DIV0: "#DIV/0!", VALUE: "#VALUE!", REF: "#REF!", NAME: "#NAME?",
  CIRC: "#CIRC!", NULL: "#NULL!", NA: "#N/A", NUM: "#NUM!",
};
export const isErr = (v) => typeof v === "string" && v.startsWith("#") && (v.endsWith("!") || v.endsWith("?"));

// ─── HELPERS ────────────────────────────────────────────────────────────────
const toNum = (v) => { if (typeof v === "number") return v; if (typeof v === "boolean") return v ? 1 : 0; if (v === "" || v === null || v === undefined) return 0; const n = Number(v); return isNaN(n) ? null : n; };
const toStr = (v) => v === null || v === undefined ? "" : String(v);
const toBool = (v) => { if (typeof v === "boolean") return v; if (typeof v === "number") return v !== 0; if (typeof v === "string") return v.toLowerCase() === "true"; return !!v; };
const toDate = (v) => { if (v instanceof Date) return v; const d = new Date(v); return isNaN(d) ? null : d; };
const isoDate = (d) => d ? d.toISOString().slice(0, 10) : ERR.VALUE;

// ═══════════════════════════════════════════════════════════════════════════
//  TOKENIZER
// ═══════════════════════════════════════════════════════════════════════════
export function tokenize(expr) {
  const tokens = []; let i = 0;
  while (i < expr.length) {
    const ch = expr[i];
    if (ch === " ") { i++; continue; }
    // String
    if (ch === '"') { let s = ""; i++; while (i < expr.length && expr[i] !== '"') { if (expr[i] === '\\' && i + 1 < expr.length) { s += expr[i + 1]; i += 2; } else { s += expr[i]; i++; } } i++; tokens.push({ type: "string", value: s }); continue; }
    // Number
    if (/[0-9.]/.test(ch)) { let n = ""; while (i < expr.length && /[0-9.eE\-+]/.test(expr[i]) && !(expr[i] === '-' && n.length > 0 && !n.endsWith('e') && !n.endsWith('E'))) { n += expr[i]; i++; } if (expr[i] === "%") { tokens.push({ type: "number", value: parseFloat(n) / 100 }); i++; } else tokens.push({ type: "number", value: parseFloat(n) }); continue; }
    // Word (cell ref, range, function, boolean, name)
    if (/[A-Za-z_$]/.test(ch)) {
      let w = ""; while (i < expr.length && /[A-Za-z0-9_.$]/.test(expr[i])) { w += expr[i]; i++; }
      const up = w.toUpperCase();
      // Range: A1:B10
      if (expr[i] === ":" && /^[A-Z]{1,2}\d+$/i.test(w)) {
        i++; let w2 = ""; while (i < expr.length && /[A-Za-z0-9]/.test(expr[i])) { w2 += expr[i]; i++; }
        tokens.push({ type: "range", value: up + ":" + w2.toUpperCase() }); continue;
      }
      // Entire column range: A:B
      if (expr[i] === ":" && /^[A-Z]{1,2}$/i.test(w)) {
        i++; let w2 = ""; while (i < expr.length && /[A-Z]/i.test(expr[i])) { w2 += expr[i]; i++; }
        tokens.push({ type: "colrange", value: up + ":" + w2.toUpperCase() }); continue;
      }
      // Cell ref
      if (/^[A-Z]{1,2}\d+$/i.test(w) && expr[i] !== "(") { tokens.push({ type: "cell", value: up }); continue; }
      // Function call
      if (expr[i] === "(") { tokens.push({ type: "func", value: up }); continue; }
      // Boolean
      if (up === "TRUE") { tokens.push({ type: "boolean", value: true }); continue; }
      if (up === "FALSE") { tokens.push({ type: "boolean", value: false }); continue; }
      // Named reference
      tokens.push({ type: "name", value: up }); continue;
    }
    // Operators
    if ("+-*/^&=<>".includes(ch)) {
      if (ch === "<" && expr[i + 1] === ">") { tokens.push({ type: "op", value: "<>" }); i += 2; continue; }
      if (ch === "<" && expr[i + 1] === "=") { tokens.push({ type: "op", value: "<=" }); i += 2; continue; }
      if (ch === ">" && expr[i + 1] === "=") { tokens.push({ type: "op", value: ">=" }); i += 2; continue; }
      // Unary minus
      if (ch === "-" && (tokens.length === 0 || ["op", "lparen", "comma"].includes(tokens[tokens.length - 1]?.type))) {
        tokens.push({ type: "unary", value: "-" }); i++; continue;
      }
      // Unary plus
      if (ch === "+" && (tokens.length === 0 || ["op", "lparen", "comma"].includes(tokens[tokens.length - 1]?.type))) {
        i++; continue; // skip unary plus
      }
      tokens.push({ type: "op", value: ch }); i++; continue;
    }
    if (ch === "(") { tokens.push({ type: "lparen" }); i++; continue; }
    if (ch === ")") { tokens.push({ type: "rparen" }); i++; continue; }
    if (ch === ",") { tokens.push({ type: "comma" }); i++; continue; }
    if (ch === ";") { tokens.push({ type: "comma" }); i++; continue; } // semicolon as arg separator (locale support)
    i++; // skip unknown
  }
  return tokens;
}

// ═══════════════════════════════════════════════════════════════════════════
//  PARSER (Recursive Descent)
// ═══════════════════════════════════════════════════════════════════════════
export function parse(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = (type) => { if (tokens[pos]?.type === type) return tokens[pos++]; return null; };
  const next = () => tokens[pos++];

  function parseExpr() { return parseCompare(); }
  function parseCompare() { let l = parseConcat(); while (peek()?.type === "op" && ["=","<>","<",">","<=",">="].includes(peek().value)) { const op = next().value; l = { type: "binary", op, left: l, right: parseConcat() }; } return l; }
  function parseConcat() { let l = parseAdd(); while (peek()?.type === "op" && peek().value === "&") { next(); l = { type: "binary", op: "&", left: l, right: parseAdd() }; } return l; }
  function parseAdd() { let l = parseMul(); while (peek()?.type === "op" && "+-".includes(peek().value)) { const op = next().value; l = { type: "binary", op, left: l, right: parseMul() }; } return l; }
  function parseMul() { let l = parsePow(); while (peek()?.type === "op" && "*/".includes(peek().value)) { const op = next().value; l = { type: "binary", op, left: l, right: parsePow() }; } return l; }
  function parsePow() { let l = parseUnary(); while (peek()?.type === "op" && peek().value === "^") { next(); l = { type: "binary", op: "^", left: l, right: parseUnary() }; } return l; }
  function parseUnary() { if (peek()?.type === "unary" && peek().value === "-") { next(); return { type: "unary", op: "-", expr: parsePrimary() }; } return parsePrimary(); }

  function parsePrimary() {
    const t = peek();
    if (!t) return { type: "literal", value: 0 };
    if (t.type === "number" || t.type === "string" || t.type === "boolean") { next(); return { type: "literal", value: t.value }; }
    if (t.type === "cell") { next(); return { type: "cell", ref: t.value }; }
    if (t.type === "range") { next(); return { type: "range", ref: t.value }; }
    if (t.type === "colrange") { next(); return { type: "colrange", ref: t.value }; }
    if (t.type === "func") {
      const name = next().value; consume("lparen"); const args = [];
      if (peek()?.type !== "rparen") { args.push(parseExpr()); while (peek()?.type === "comma") { next(); args.push(parseExpr()); } }
      consume("rparen"); return { type: "func", name, args };
    }
    if (t.type === "lparen") { next(); const e = parseExpr(); consume("rparen"); return e; }
    if (t.type === "name") { next(); return { type: "literal", value: ERR.NAME }; }
    next(); return { type: "literal", value: ERR.VALUE };
  }

  return parseExpr();
}

// ═══════════════════════════════════════════════════════════════════════════
//  DEPENDENCY EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════
export function extractDeps(ast, maxRow = 999) {
  const deps = new Set();
  function walk(n) {
    if (!n) return;
    if (n.type === "cell") deps.add(n.ref);
    if (n.type === "range") {
      const [s, e] = n.ref.split(":"); const a = parseCellRef(s), b = parseCellRef(e);
      if (a && b) { for (let r = a.row; r <= Math.min(b.row, maxRow); r++) for (let c = a.col; c <= b.col; c++) deps.add(cellKey(r, c)); }
    }
    if (n.left) walk(n.left); if (n.right) walk(n.right); if (n.expr) walk(n.expr);
    if (n.args) n.args.forEach(walk);
  }
  walk(ast); return deps;
}

// ═══════════════════════════════════════════════════════════════════════════
//  RANGE RESOLVER
// ═══════════════════════════════════════════════════════════════════════════
export function resolveRange(ref, cells, maxRow = 999) {
  const [s, e] = ref.split(":"); const a = parseCellRef(s), b = parseCellRef(e);
  if (!a || !b) return [];
  const v = [];
  for (let r = a.row; r <= Math.min(b.row, maxRow); r++)
    for (let c = a.col; c <= b.col; c++) {
      const val = cells[cellKey(r, c)]?.computed;
      v.push(val !== undefined && val !== null ? val : "");
    }
  return v;
}

function resolveRangeWithKeys(ref, cells, maxRow = 999) {
  const [s, e] = ref.split(":"); const a = parseCellRef(s), b = parseCellRef(e);
  if (!a || !b) return [];
  const v = [];
  for (let r = a.row; r <= Math.min(b.row, maxRow); r++)
    for (let c = a.col; c <= b.col; c++) {
      const key = cellKey(r, c);
      v.push({ key, value: cells[key]?.computed ?? "" });
    }
  return v;
}

// ═══════════════════════════════════════════════════════════════════════════
//  EVALUATOR
// ═══════════════════════════════════════════════════════════════════════════
export function evaluate(ast, cells, ctx = {}) {
  if (!ast) return 0;
  const { contacts, currentCell, visited = new Set() } = ctx;

  if (ast.type === "literal") return ast.value;

  if (ast.type === "cell") {
    if (visited.has(ast.ref)) return ERR.CIRC;
    const cell = cells[ast.ref];
    if (!cell) return 0;
    return cell.computed !== undefined ? cell.computed : 0;
  }

  if (ast.type === "unary" && ast.op === "-") {
    const v = evaluate(ast.expr, cells, ctx);
    if (isErr(v)) return v;
    return -(toNum(v) ?? 0);
  }

  if (ast.type === "binary") {
    const l = evaluate(ast.left, cells, ctx);
    const r = evaluate(ast.right, cells, ctx);
    if (isErr(l)) return l; if (isErr(r)) return r;
    const ln = toNum(l), rn = toNum(r);
    switch (ast.op) {
      case "+": return (ln ?? 0) + (rn ?? 0);
      case "-": return (ln ?? 0) - (rn ?? 0);
      case "*": return (ln ?? 0) * (rn ?? 0);
      case "/": return rn === 0 ? ERR.DIV0 : (ln ?? 0) / rn;
      case "^": return Math.pow(ln ?? 0, rn ?? 0);
      case "&": return toStr(l) + toStr(r);
      case "=": return l == r; // eslint-disable-line
      case "<>": return l != r; // eslint-disable-line
      case "<": return (ln !== null && rn !== null) ? ln < rn : toStr(l).toLowerCase() < toStr(r).toLowerCase();
      case ">": return (ln !== null && rn !== null) ? ln > rn : toStr(l).toLowerCase() > toStr(r).toLowerCase();
      case "<=": return (ln !== null && rn !== null) ? ln <= rn : toStr(l).toLowerCase() <= toStr(r).toLowerCase();
      case ">=": return (ln !== null && rn !== null) ? ln >= rn : toStr(l).toLowerCase() >= toStr(r).toLowerCase();
      default: return ERR.VALUE;
    }
  }

  if (ast.type === "range") return resolveRange(ast.ref, cells);
  if (ast.type === "colrange") return []; // Not yet supported at eval level

  if (ast.type === "func") return evalFunc(ast.name, ast.args, cells, ctx);

  return ERR.VALUE;
}

// ═══════════════════════════════════════════════════════════════════════════
//  FUNCTION HELPERS
// ═══════════════════════════════════════════════════════════════════════════
function ev(ast, cells, ctx) { return evaluate(ast, cells, ctx); }

function getNums(args, cells, ctx) {
  const r = [];
  for (const a of args) {
    if (a.type === "range") {
      resolveRange(a.ref, cells).forEach(v => { const n = toNum(v); if (n !== null && typeof v !== "boolean") r.push(n); });
    } else {
      const v = ev(a, cells, ctx); const n = toNum(v);
      if (n !== null && typeof v !== "boolean" && v !== "") r.push(n);
    }
  }
  return r;
}

function getAllVals(args, cells, ctx) {
  const r = [];
  for (const a of args) {
    if (a.type === "range") r.push(...resolveRange(a.ref, cells));
    else r.push(ev(a, cells, ctx));
  }
  return r;
}

function matchCriteria(value, criteria) {
  const c = toStr(criteria);
  if (c.startsWith(">=")) return (toNum(value) ?? -Infinity) >= Number(c.slice(2));
  if (c.startsWith("<=")) return (toNum(value) ?? Infinity) <= Number(c.slice(2));
  if (c.startsWith("<>")) return toStr(value).toLowerCase() !== c.slice(2).toLowerCase();
  if (c.startsWith(">")) return (toNum(value) ?? -Infinity) > Number(c.slice(1));
  if (c.startsWith("<")) return (toNum(value) ?? Infinity) < Number(c.slice(1));
  if (c.startsWith("=")) return toStr(value).toLowerCase() === c.slice(1).toLowerCase();
  // Wildcard support: * and ?
  if (c.includes("*") || c.includes("?")) {
    const pattern = c.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*").replace(/\?/g, ".");
    return new RegExp("^" + pattern + "$", "i").test(toStr(value));
  }
  return toStr(value).toLowerCase() === c.toLowerCase();
}

// ═══════════════════════════════════════════════════════════════════════════
//  ALL FUNCTIONS — Organized by category
// ═══════════════════════════════════════════════════════════════════════════
function evalFunc(name, args, cells, ctx) {
  const e = (a) => ev(a, cells, ctx);
  const n = () => getNums(args, cells, ctx);
  const all = () => getAllVals(args, cells, ctx);

  // ═══ MATH ═════════════════════════════════════════════════════════════
  switch (name) {
  case "SUM": return n().reduce((a, b) => a + b, 0);
  case "AVERAGE": { const nums = n(); return nums.length === 0 ? ERR.DIV0 : nums.reduce((a, b) => a + b, 0) / nums.length; }
  case "AVERAGEA": { const vals = all().filter(v => v !== "" && v !== null); if (!vals.length) return ERR.DIV0; return vals.reduce((s, v) => s + (toNum(v) ?? 0), 0) / vals.length; }
  case "COUNT": return n().length;
  case "COUNTA": return all().filter(v => v !== "" && v !== null && v !== undefined).length;
  case "COUNTBLANK": return all().filter(v => v === "" || v === null || v === undefined).length;
  case "MIN": { const nums = n(); return nums.length === 0 ? 0 : Math.min(...nums); }
  case "MINA": { const vals = all().filter(v => v !== "").map(v => toNum(v) ?? 0); return vals.length ? Math.min(...vals) : 0; }
  case "MAX": { const nums = n(); return nums.length === 0 ? 0 : Math.max(...nums); }
  case "MAXA": { const vals = all().filter(v => v !== "").map(v => toNum(v) ?? 0); return vals.length ? Math.max(...vals) : 0; }
  case "ROUND": { const v = toNum(e(args[0])) ?? 0; const d = toNum(e(args[1])) ?? 0; const f = Math.pow(10, d); return Math.round(v * f) / f; }
  case "ROUNDUP": { const v = toNum(e(args[0])) ?? 0; const d = toNum(e(args[1])) ?? 0; const f = Math.pow(10, d); return Math.ceil(v * f) / f; }
  case "ROUNDDOWN": { const v = toNum(e(args[0])) ?? 0; const d = toNum(e(args[1])) ?? 0; const f = Math.pow(10, d); return Math.floor(v * f) / f; }
  case "MROUND": { const v = toNum(e(args[0])) ?? 0; const m = toNum(e(args[1])) ?? 1; return Math.round(v / m) * m; }
  case "ABS": return Math.abs(toNum(e(args[0])) ?? 0);
  case "INT": return Math.floor(toNum(e(args[0])) ?? 0);
  case "TRUNC": { const v = toNum(e(args[0])) ?? 0; const d = toNum(e(args[1])) ?? 0; const f = Math.pow(10, d); return Math.trunc(v * f) / f; }
  case "MOD": { const a = toNum(e(args[0])) ?? 0; const b = toNum(e(args[1])) ?? 0; return b === 0 ? ERR.DIV0 : a - b * Math.floor(a / b); }
  case "POWER": return Math.pow(toNum(e(args[0])) ?? 0, toNum(e(args[1])) ?? 0);
  case "SQRT": { const v = toNum(e(args[0])) ?? 0; return v < 0 ? ERR.NUM : Math.sqrt(v); }
  case "SQRTPI": { const v = toNum(e(args[0])) ?? 0; return v < 0 ? ERR.NUM : Math.sqrt(v * Math.PI); }
  case "PRODUCT": { const nums = n(); return nums.length === 0 ? 0 : nums.reduce((a, b) => a * b, 1); }
  case "SUMPRODUCT": {
    if (args.length < 2 || args[0].type !== "range") return ERR.VALUE;
    const ranges = args.map(a => a.type === "range" ? resolveRange(a.ref, cells) : [e(a)]);
    const len = ranges[0].length; let sum = 0;
    for (let i = 0; i < len; i++) { let prod = 1; for (const r of ranges) prod *= toNum(r[i]) ?? 0; sum += prod; }
    return sum;
  }
  case "SUMSQ": return n().reduce((s, v) => s + v * v, 0);
  case "CEILING": case "CEILING.MATH": { const v = toNum(e(args[0])) ?? 0; const s = toNum(e(args[1])) ?? 1; return s === 0 ? 0 : Math.ceil(v / s) * s; }
  case "FLOOR": case "FLOOR.MATH": { const v = toNum(e(args[0])) ?? 0; const s = toNum(e(args[1])) ?? 1; return s === 0 ? 0 : Math.floor(v / s) * s; }
  case "SIGN": { const v = toNum(e(args[0])) ?? 0; return v > 0 ? 1 : v < 0 ? -1 : 0; }
  case "RAND": return Math.random();
  case "RANDBETWEEN": { const lo = toNum(e(args[0])) ?? 0; const hi = toNum(e(args[1])) ?? 1; return Math.floor(Math.random() * (hi - lo + 1)) + lo; }
  case "LOG": { const v = toNum(e(args[0])) ?? 0; const b = args[1] ? toNum(e(args[1])) ?? 10 : 10; return v <= 0 ? ERR.NUM : Math.log(v) / Math.log(b); }
  case "LOG10": { const v = toNum(e(args[0])) ?? 0; return v <= 0 ? ERR.NUM : Math.log10(v); }
  case "LN": { const v = toNum(e(args[0])) ?? 0; return v <= 0 ? ERR.NUM : Math.log(v); }
  case "EXP": return Math.exp(toNum(e(args[0])) ?? 0);
  case "PI": return Math.PI;
  case "SIN": return Math.sin(toNum(e(args[0])) ?? 0);
  case "COS": return Math.cos(toNum(e(args[0])) ?? 0);
  case "TAN": return Math.tan(toNum(e(args[0])) ?? 0);
  case "ASIN": return Math.asin(toNum(e(args[0])) ?? 0);
  case "ACOS": return Math.acos(toNum(e(args[0])) ?? 0);
  case "ATAN": return Math.atan(toNum(e(args[0])) ?? 0);
  case "ATAN2": return Math.atan2(toNum(e(args[0])) ?? 0, toNum(e(args[1])) ?? 0);
  case "SINH": return Math.sinh(toNum(e(args[0])) ?? 0);
  case "COSH": return Math.cosh(toNum(e(args[0])) ?? 0);
  case "TANH": return Math.tanh(toNum(e(args[0])) ?? 0);
  case "ASINH": return Math.asinh(toNum(e(args[0])) ?? 0);
  case "ACOSH": return Math.acosh(toNum(e(args[0])) ?? 0);
  case "ATANH": return Math.atanh(toNum(e(args[0])) ?? 0);
  case "DEGREES": return (toNum(e(args[0])) ?? 0) * (180 / Math.PI);
  case "RADIANS": return (toNum(e(args[0])) ?? 0) * (Math.PI / 180);
  case "EVEN": { const v = toNum(e(args[0])) ?? 0; const r = Math.ceil(Math.abs(v)); return (r % 2 === 0 ? r : r + 1) * Math.sign(v || 1); }
  case "ODD": { const v = toNum(e(args[0])) ?? 0; const r = Math.ceil(Math.abs(v)); return (r % 2 === 1 ? r : r + 1) * Math.sign(v || 1); }
  case "FACT": { const v = Math.floor(toNum(e(args[0])) ?? 0); if (v < 0) return ERR.NUM; let f = 1; for (let i = 2; i <= v; i++) f *= i; return f; }
  case "FACTDOUBLE": { const v = Math.floor(toNum(e(args[0])) ?? 0); if (v < 0) return ERR.NUM; let f = 1; for (let i = v; i > 0; i -= 2) f *= i; return f; }
  case "COMBIN": { const n2 = Math.floor(toNum(e(args[0])) ?? 0); const k = Math.floor(toNum(e(args[1])) ?? 0); if (k > n2 || k < 0) return ERR.NUM; let r = 1; for (let i = 0; i < k; i++) r = r * (n2 - i) / (i + 1); return Math.round(r); }
  case "COMBINA": { const n2 = Math.floor(toNum(e(args[0])) ?? 0); const k = Math.floor(toNum(e(args[1])) ?? 0); let r = 1; for (let i = 0; i < k; i++) r = r * (n2 + k - 1 - i) / (i + 1); return Math.round(r); }
  case "GCD": { const nums = n(); const gcd = (a, b) => b === 0 ? a : gcd(b, a % b); return nums.reduce((a, b) => gcd(Math.abs(a), Math.abs(b))); }
  case "LCM": { const nums = n(); const gcd = (a, b) => b === 0 ? a : gcd(b, a % b); return nums.reduce((a, b) => Math.abs(a * b) / gcd(Math.abs(a), Math.abs(b))); }
  case "QUOTIENT": return Math.trunc((toNum(e(args[0])) ?? 0) / (toNum(e(args[1])) ?? 1));
  case "ISODD": return (Math.floor(toNum(e(args[0])) ?? 0) % 2) !== 0;
  case "ISEVEN": return (Math.floor(toNum(e(args[0])) ?? 0) % 2) === 0;
  case "SERIESSUM": {
    const x = toNum(e(args[0])) ?? 0; const n2 = toNum(e(args[1])) ?? 0; const m = toNum(e(args[2])) ?? 0;
    const coeffs = args[3]?.type === "range" ? resolveRange(args[3].ref, cells) : [e(args[3])];
    let sum = 0; coeffs.forEach((c, i) => { sum += (toNum(c) ?? 0) * Math.pow(x, n2 + i * m); }); return sum;
  }
  case "SUBTOTAL": {
    const fn = toNum(e(args[0])) ?? 0; const vals = args.slice(1);
    const numsArr = getNums(vals, cells, ctx); const allArr = getAllVals(vals, cells, ctx);
    switch (fn) {
      case 1: case 101: return numsArr.reduce((a, b) => a + b, 0) / (numsArr.length || 1); // AVERAGE
      case 2: case 102: return numsArr.length; // COUNT
      case 3: case 103: return allArr.filter(v => v !== "" && v !== null).length; // COUNTA
      case 4: case 104: return numsArr.length ? Math.max(...numsArr) : 0; // MAX
      case 5: case 105: return numsArr.length ? Math.min(...numsArr) : 0; // MIN
      case 6: case 106: return numsArr.reduce((a, b) => a * b, 1); // PRODUCT
      case 9: case 109: return numsArr.reduce((a, b) => a + b, 0); // SUM
      default: return numsArr.reduce((a, b) => a + b, 0);
    }
  }

  // ═══ TEXT ══════════════════════════════════════════════════════════════
  case "CONCATENATE": case "CONCAT": return args.map(a => toStr(e(a))).join("");
  case "TEXTJOIN": { const delim = toStr(e(args[0])); const skip = toBool(e(args[1])); const vals = getAllVals(args.slice(2), cells, ctx); return vals.filter(v => !skip || (v !== "" && v !== null)).map(v => toStr(v)).join(delim); }
  case "LEFT": { const s = toStr(e(args[0])); return s.slice(0, toNum(e(args[1])) ?? 1); }
  case "RIGHT": { const s = toStr(e(args[0])); return s.slice(-(toNum(e(args[1])) ?? 1)); }
  case "MID": { const s = toStr(e(args[0])); const st = (toNum(e(args[1])) ?? 1) - 1; return s.slice(st, st + (toNum(e(args[2])) ?? 1)); }
  case "LEN": return toStr(e(args[0])).length;
  case "UPPER": return toStr(e(args[0])).toUpperCase();
  case "LOWER": return toStr(e(args[0])).toLowerCase();
  case "PROPER": return toStr(e(args[0])).replace(/\b\w/g, c => c.toUpperCase());
  case "TRIM": return toStr(e(args[0])).trim().replace(/\s+/g, " ");
  case "CLEAN": return toStr(e(args[0])).replace(/[\x00-\x1F]/g, "");
  case "SUBSTITUTE": { const s = toStr(e(args[0])); const old = toStr(e(args[1])); const rep = toStr(e(args[2])); const occ = args[3] ? toNum(e(args[3])) : null; if (occ) { let c = 0; return s.replace(new RegExp(old.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), (m) => (++c === occ) ? rep : m); } return s.split(old).join(rep); }
  case "REPLACE": { const s = toStr(e(args[0])); const st = (toNum(e(args[1])) ?? 1) - 1; const n2 = toNum(e(args[2])) ?? 1; return s.slice(0, st) + toStr(e(args[3])) + s.slice(st + n2); }
  case "FIND": { const f = toStr(e(args[0])); const w = toStr(e(args[1])); const st = (toNum(e(args[2])) ?? 1) - 1; const idx = w.indexOf(f, st); return idx === -1 ? ERR.VALUE : idx + 1; }
  case "SEARCH": { const f = toStr(e(args[0])).toLowerCase(); const w = toStr(e(args[1])).toLowerCase(); const st = (toNum(e(args[2])) ?? 1) - 1; const idx = w.indexOf(f, st); return idx === -1 ? ERR.VALUE : idx + 1; }
  case "REPT": return toStr(e(args[0])).repeat(Math.max(0, toNum(e(args[1])) ?? 0));
  case "VALUE": { const v = toNum(e(args[0])); return v === null ? ERR.VALUE : v; }
  case "T": { const v = e(args[0]); return typeof v === "string" ? v : ""; }
  case "N": { const v = e(args[0]); if (typeof v === "number") return v; if (typeof v === "boolean") return v ? 1 : 0; return 0; }
  case "FIXED": { const v = toNum(e(args[0])) ?? 0; const d = toNum(e(args[1])) ?? 2; const noComma = args[2] ? toBool(e(args[2])) : false; const fixed = v.toFixed(d); return noComma ? fixed : Number(fixed).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }); }
  case "DOLLAR": { const v = toNum(e(args[0])) ?? 0; const d = toNum(e(args[1])) ?? 2; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }); }
  case "TEXT": {
    const v = e(args[0]); const fmt = toStr(e(args[1])); const num = toNum(v);
    if (num === null) return toStr(v);
    if (fmt.includes("$")) return "$" + num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (fmt.includes("%")) return (num * 100).toFixed((fmt.split(".")[1] || "").length) + "%";
    if (fmt.includes("#") || fmt.includes("0")) { const dec = (fmt.split(".")[1] || "").length; return num.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }); }
    return toStr(v);
  }
  case "CHAR": return String.fromCharCode(toNum(e(args[0])) ?? 0);
  case "CODE": { const s = toStr(e(args[0])); return s.length > 0 ? s.charCodeAt(0) : ERR.VALUE; }
  case "EXACT": return toStr(e(args[0])) === toStr(e(args[1]));
  case "REGEXMATCH": { try { return new RegExp(toStr(e(args[1]))).test(toStr(e(args[0]))); } catch { return ERR.VALUE; } }
  case "REGEXEXTRACT": { try { const m = toStr(e(args[0])).match(new RegExp(toStr(e(args[1])))); return m ? (m[1] || m[0]) : ERR.NA; } catch { return ERR.VALUE; } }
  case "REGEXREPLACE": { try { return toStr(e(args[0])).replace(new RegExp(toStr(e(args[1])), "g"), toStr(e(args[2]))); } catch { return ERR.VALUE; } }
  case "SPLIT": { const parts = toStr(e(args[0])).split(toStr(e(args[1]))); return parts[0]; } // returns first part (array spill not supported yet)
  case "JOIN": return getAllVals(args.slice(1), cells, ctx).join(toStr(e(args[0])));
  case "ARRAYFORMULA": return args[0] ? e(args[0]) : ERR.VALUE; // simplified — evaluates inner formula
  case "HYPERLINK": return toStr(e(args[1] || args[0])); // display text
  case "ROMAN": { let v = Math.floor(toNum(e(args[0])) ?? 0); if (v <= 0 || v > 3999) return ERR.VALUE; const map = [[1000,"M"],[900,"CM"],[500,"D"],[400,"CD"],[100,"C"],[90,"XC"],[50,"L"],[40,"XL"],[10,"X"],[9,"IX"],[5,"V"],[4,"IV"],[1,"I"]]; let r = ""; for (const [n2, s] of map) { while (v >= n2) { r += s; v -= n2; } } return r; }
  case "ARABIC": { const s = toStr(e(args[0])).toUpperCase(); const map = { M:1000, D:500, C:100, L:50, X:10, V:5, I:1 }; let r = 0; for (let i = 0; i < s.length; i++) { const cur = map[s[i]] || 0; const next = map[s[i+1]] || 0; r += cur < next ? -cur : cur; } return r; }
  case "BASE": { const v = Math.floor(toNum(e(args[0])) ?? 0); const b = Math.floor(toNum(e(args[1])) ?? 10); const pad = args[2] ? Math.floor(toNum(e(args[2])) ?? 0) : 0; return v.toString(b).toUpperCase().padStart(pad, "0"); }
  case "DECIMAL": return parseInt(toStr(e(args[0])), toNum(e(args[1])) ?? 10);

  // ═══ LOGICAL ══════════════════════════════════════════════════════════
  case "IF": { const c = e(args[0]); if (isErr(c)) return c; return toBool(c) ? e(args[1]) : (args[2] ? e(args[2]) : false); }
  case "IFS": { for (let i = 0; i < args.length; i += 2) { const c = e(args[i]); if (isErr(c)) return c; if (toBool(c)) return e(args[i + 1]); } return ERR.NA; }
  case "AND": { for (const a of args) { const v = e(a); if (isErr(v)) return v; if (!toBool(v)) return false; } return true; }
  case "OR": { for (const a of args) { const v = e(a); if (isErr(v)) return v; if (toBool(v)) return true; } return false; }
  case "XOR": { let count = 0; for (const a of args) { const v = e(a); if (isErr(v)) return v; if (toBool(v)) count++; } return count % 2 === 1; }
  case "NOT": { const v = e(args[0]); if (isErr(v)) return v; return !toBool(v); }
  case "IFERROR": { try { const v = e(args[0]); return isErr(v) ? e(args[1]) : v; } catch { return e(args[1]); } }
  case "IFNA": { const v = e(args[0]); return v === ERR.NA ? e(args[1]) : v; }
  case "SWITCH": { const val = e(args[0]); for (let i = 1; i < args.length - 1; i += 2) { if (e(args[i]) == val) return e(args[i + 1]); } return args.length % 2 === 0 ? e(args[args.length - 1]) : ERR.NA; } // eslint-disable-line
  case "CHOOSE": { const idx = Math.floor(toNum(e(args[0])) ?? 0); if (idx < 1 || idx >= args.length) return ERR.VALUE; return e(args[idx]); }
  case "TRUE": return true;
  case "FALSE": return false;

  // ═══ INFO ═════════════════════════════════════════════════════════════
  case "ISBLANK": { const v = e(args[0]); return v === "" || v === null || v === undefined; }
  case "ISNUMBER": { const v = e(args[0]); return typeof v === "number" && !isNaN(v) && !isErr(v); }
  case "ISTEXT": { const v = e(args[0]); return typeof v === "string" && !isErr(v); }
  case "ISLOGICAL": return typeof e(args[0]) === "boolean";
  case "ISERROR": return isErr(e(args[0]));
  case "ISERR": { const v = e(args[0]); return isErr(v) && v !== ERR.NA; }
  case "ISNA": return e(args[0]) === ERR.NA;
  case "ISREF": return args[0]?.type === "cell" || args[0]?.type === "range";
  case "ISFORMULA": { if (args[0]?.type !== "cell") return false; const raw = cells[args[0].ref]?.raw; return typeof raw === "string" && raw.startsWith("="); }
  case "ISODD": return Math.floor(toNum(e(args[0])) ?? 0) % 2 !== 0;
  case "ISEVEN": return Math.floor(toNum(e(args[0])) ?? 0) % 2 === 0;
  case "TYPE": { const v = e(args[0]); if (typeof v === "number") return 1; if (typeof v === "string") return 2; if (typeof v === "boolean") return 4; if (isErr(v)) return 16; return 0; }
  case "ERROR.TYPE": { const v = e(args[0]); const map = { [ERR.NULL]: 1, [ERR.DIV0]: 2, [ERR.VALUE]: 3, [ERR.REF]: 4, [ERR.NAME]: 5, [ERR.NUM]: 6, [ERR.NA]: 7 }; return map[v] || ERR.NA; }
  case "CELL": return ""; // stub
  case "NA": return ERR.NA;

  // ═══ DATE & TIME ══════════════════════════════════════════════════════
  case "TODAY": return new Date().toISOString().slice(0, 10);
  case "NOW": return new Date().toISOString().slice(0, 16).replace("T", " ");
  case "DATE": return isoDate(new Date(toNum(e(args[0])) ?? 2000, (toNum(e(args[1])) ?? 1) - 1, toNum(e(args[2])) ?? 1));
  case "DATEVALUE": { const d = toDate(e(args[0])); return d ? isoDate(d) : ERR.VALUE; }
  case "TIME": { const h = toNum(e(args[0])) ?? 0; const m = toNum(e(args[1])) ?? 0; const s = toNum(e(args[2])) ?? 0; return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0") + ":" + String(Math.floor(s)).padStart(2, "0"); }
  case "TIMEVALUE": { const t = toStr(e(args[0])); const m = t.match(/(\d+):(\d+):?(\d+)?/); if (!m) return ERR.VALUE; return ((+m[1]) * 3600 + (+m[2]) * 60 + (+m[3] || 0)) / 86400; }
  case "YEAR": { const d = toDate(e(args[0])); return d ? d.getFullYear() : ERR.VALUE; }
  case "MONTH": { const d = toDate(e(args[0])); return d ? d.getMonth() + 1 : ERR.VALUE; }
  case "DAY": { const d = toDate(e(args[0])); return d ? d.getDate() : ERR.VALUE; }
  case "WEEKDAY": { const d = toDate(e(args[0])); return d ? d.getDay() + 1 : ERR.VALUE; }
  case "WEEKNUM": { const d = toDate(e(args[0])); if (!d) return ERR.VALUE; const jan1 = new Date(d.getFullYear(), 0, 1); return Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7); }
  case "ISOWEEKNUM": { const d = toDate(e(args[0])); if (!d) return ERR.VALUE; const tmp = new Date(d); tmp.setDate(tmp.getDate() + 3 - (tmp.getDay() + 6) % 7); const jan4 = new Date(tmp.getFullYear(), 0, 4); return 1 + Math.round((tmp - jan4) / 86400000 / 7); }
  case "HOUR": { const d = toDate(e(args[0])); return d ? d.getHours() : ERR.VALUE; }
  case "MINUTE": { const d = toDate(e(args[0])); return d ? d.getMinutes() : ERR.VALUE; }
  case "SECOND": { const d = toDate(e(args[0])); return d ? d.getSeconds() : ERR.VALUE; }
  case "DATEDIF": {
    const d1 = toDate(e(args[0])); const d2 = toDate(e(args[1])); const u = toStr(e(args[2])).toUpperCase();
    if (!d1 || !d2) return ERR.VALUE;
    if (u === "D") return Math.floor((d2 - d1) / 86400000);
    if (u === "M") return (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth();
    if (u === "Y") return d2.getFullYear() - d1.getFullYear();
    return ERR.VALUE;
  }
  case "DAYS": { const d1 = toDate(e(args[0])); const d2 = toDate(e(args[1])); return d1 && d2 ? Math.floor((d1 - d2) / 86400000) : ERR.VALUE; }
  case "DAYS360": { const d1 = toDate(e(args[0])); const d2 = toDate(e(args[1])); if (!d1 || !d2) return ERR.VALUE; return ((d2.getFullYear() - d1.getFullYear()) * 360 + (d2.getMonth() - d1.getMonth()) * 30 + Math.min(d2.getDate(), 30) - Math.min(d1.getDate(), 30)); }
  case "EDATE": { const d = toDate(e(args[0])); if (!d) return ERR.VALUE; const r = new Date(d); r.setMonth(r.getMonth() + (toNum(e(args[1])) ?? 0)); return isoDate(r); }
  case "EOMONTH": { const d = toDate(e(args[0])); if (!d) return ERR.VALUE; return isoDate(new Date(d.getFullYear(), d.getMonth() + (toNum(e(args[1])) ?? 0) + 1, 0)); }
  case "NETWORKDAYS": case "NETWORKDAYS.INTL": {
    const d1 = toDate(e(args[0])); const d2 = toDate(e(args[1]));
    if (!d1 || !d2) return ERR.VALUE;
    let c = 0; const cur = new Date(d1);
    while (cur <= d2) { const day = cur.getDay(); if (day !== 0 && day !== 6) c++; cur.setDate(cur.getDate() + 1); }
    return c;
  }
  case "WORKDAY": case "WORKDAY.INTL": {
    const d = toDate(e(args[0])); let days = toNum(e(args[1])) ?? 0;
    if (!d) return ERR.VALUE;
    const r = new Date(d); const dir = days > 0 ? 1 : -1; days = Math.abs(days);
    while (days > 0) { r.setDate(r.getDate() + dir); const day = r.getDay(); if (day !== 0 && day !== 6) days--; }
    return isoDate(r);
  }
  case "YEARFRAC": { const d1 = toDate(e(args[0])); const d2 = toDate(e(args[1])); if (!d1 || !d2) return ERR.VALUE; return Math.abs(d2 - d1) / (365.25 * 86400000); }

  // ═══ STATISTICAL ══════════════════════════════════════════════════════
  case "MEDIAN": { const nums = n().sort((a, b) => a - b); if (!nums.length) return ERR.NA; const m = Math.floor(nums.length / 2); return nums.length % 2 ? nums[m] : (nums[m - 1] + nums[m]) / 2; }
  case "MODE": case "MODE.SNGL": { const nums = n(); const freq = {}; nums.forEach(v => freq[v] = (freq[v] || 0) + 1); let maxF = 0, mode = ERR.NA; Object.entries(freq).forEach(([k, f]) => { if (f > maxF) { maxF = f; mode = Number(k); } }); return maxF > 1 ? mode : ERR.NA; }
  case "STDEV": case "STDEV.S": { const nums = n(); if (nums.length < 2) return ERR.DIV0; const avg = nums.reduce((a, b) => a + b, 0) / nums.length; return Math.sqrt(nums.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / (nums.length - 1)); }
  case "STDEVP": case "STDEV.P": { const nums = n(); if (!nums.length) return ERR.DIV0; const avg = nums.reduce((a, b) => a + b, 0) / nums.length; return Math.sqrt(nums.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / nums.length); }
  case "VAR": case "VAR.S": { const nums = n(); if (nums.length < 2) return ERR.DIV0; const avg = nums.reduce((a, b) => a + b, 0) / nums.length; return nums.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / (nums.length - 1); }
  case "VARP": case "VAR.P": { const nums = n(); if (!nums.length) return ERR.DIV0; const avg = nums.reduce((a, b) => a + b, 0) / nums.length; return nums.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / nums.length; }
  case "RANK": case "RANK.EQ": { const v = toNum(e(args[0])); const nums = getNums([args[1]], cells, ctx).sort((a, b) => b - a); const idx = nums.indexOf(v); return idx === -1 ? ERR.NA : idx + 1; }
  case "RANK.AVG": { const v = toNum(e(args[0])); const nums = getNums([args[1]], cells, ctx).sort((a, b) => b - a); const first = nums.indexOf(v); const last = nums.lastIndexOf(v); if (first === -1) return ERR.NA; return (first + last) / 2 + 1; }
  case "LARGE": { const nums = getNums([args[0]], cells, ctx).sort((a, b) => b - a); const k = toNum(e(args[1])) ?? 1; return k > nums.length || k < 1 ? ERR.NA : nums[k - 1]; }
  case "SMALL": { const nums = getNums([args[0]], cells, ctx).sort((a, b) => a - b); const k = toNum(e(args[1])) ?? 1; return k > nums.length || k < 1 ? ERR.NA : nums[k - 1]; }
  case "PERCENTILE": case "PERCENTILE.INC": { const nums = n().sort((a, b) => a - b); const k = toNum(e(args[1])) ?? 0; if (!nums.length || k < 0 || k > 1) return ERR.NUM; const idx = k * (nums.length - 1); const lo = Math.floor(idx); const hi = Math.ceil(idx); return lo === hi ? nums[lo] : nums[lo] + (nums[hi] - nums[lo]) * (idx - lo); }
  case "QUARTILE": case "QUARTILE.INC": { const nums = n().sort((a, b) => a - b); const q = toNum(e(args[1])) ?? 0; if (!nums.length || q < 0 || q > 4) return ERR.NUM; const k = q / 4; const idx = k * (nums.length - 1); const lo = Math.floor(idx); const hi = Math.ceil(idx); return lo === hi ? nums[lo] : nums[lo] + (nums[hi] - nums[lo]) * (idx - lo); }
  case "CORREL": {
    if (args[0]?.type !== "range" || args[1]?.type !== "range") return ERR.VALUE;
    const x = resolveRange(args[0].ref, cells).map(v => toNum(v)).filter(v => v !== null);
    const y = resolveRange(args[1].ref, cells).map(v => toNum(v)).filter(v => v !== null);
    const len = Math.min(x.length, y.length); if (len < 2) return ERR.NA;
    const mx = x.slice(0, len).reduce((a, b) => a + b, 0) / len;
    const my = y.slice(0, len).reduce((a, b) => a + b, 0) / len;
    let sxy = 0, sxx = 0, syy = 0;
    for (let i = 0; i < len; i++) { sxy += (x[i] - mx) * (y[i] - my); sxx += (x[i] - mx) ** 2; syy += (y[i] - my) ** 2; }
    return sxx === 0 || syy === 0 ? ERR.DIV0 : sxy / Math.sqrt(sxx * syy);
  }
  case "COVARIANCE.P": case "COVAR": {
    const x = resolveRange(args[0].ref, cells).map(v => toNum(v)).filter(v => v !== null);
    const y = resolveRange(args[1].ref, cells).map(v => toNum(v)).filter(v => v !== null);
    const len = Math.min(x.length, y.length); if (!len) return ERR.NA;
    const mx = x.slice(0, len).reduce((a, b) => a + b, 0) / len;
    const my = y.slice(0, len).reduce((a, b) => a + b, 0) / len;
    return x.slice(0, len).reduce((s, v, i) => s + (v - mx) * (y[i] - my), 0) / len;
  }
  case "NORM.DIST": case "NORMDIST": {
    const x = toNum(e(args[0])) ?? 0; const mean = toNum(e(args[1])) ?? 0; const sd = toNum(e(args[2])) ?? 1; const cum = args[3] ? toBool(e(args[3])) : true;
    if (sd <= 0) return ERR.NUM;
    if (!cum) return Math.exp(-0.5 * ((x - mean) / sd) ** 2) / (sd * Math.sqrt(2 * Math.PI));
    // CDF approximation using error function
    return 0.5 * (1 + erf((x - mean) / (sd * Math.sqrt(2))));
  }
  case "NORM.S.DIST": case "NORMSDIST": { const z = toNum(e(args[0])) ?? 0; return 0.5 * (1 + erf(z / Math.sqrt(2))); }
  case "NORM.INV": case "NORMINV": { const p = toNum(e(args[0])) ?? 0; const mean = toNum(e(args[1])) ?? 0; const sd = toNum(e(args[2])) ?? 1; return mean + sd * Math.sqrt(2) * erfinv(2 * p - 1); }
  case "NORM.S.INV": case "NORMSINV": { const p = toNum(e(args[0])) ?? 0; return Math.sqrt(2) * erfinv(2 * p - 1); }
  case "AVERAGE.WEIGHTED": case "AVERAGEWEIGHTED": {
    if (args.length < 2) return ERR.VALUE;
    const vals = args[0].type === "range" ? resolveRange(args[0].ref, cells) : [e(args[0])];
    const weights = args[1].type === "range" ? resolveRange(args[1].ref, cells) : [e(args[1])];
    let sumW = 0, sumVW = 0;
    for (let i = 0; i < vals.length; i++) { const v = toNum(vals[i]) ?? 0; const w = toNum(weights[i]) ?? 0; sumVW += v * w; sumW += w; }
    return sumW === 0 ? ERR.DIV0 : sumVW / sumW;
  }
  case "FREQUENCY": {
    const data = args[0].type === "range" ? resolveRange(args[0].ref, cells).map(v => toNum(v)).filter(v => v !== null) : [];
    const bins = args[1].type === "range" ? resolveRange(args[1].ref, cells).map(v => toNum(v)).filter(v => v !== null).sort((a, b) => a - b) : [];
    const result = new Array(bins.length + 1).fill(0);
    for (const v of data) { let placed = false; for (let i = 0; i < bins.length; i++) { if (v <= bins[i]) { result[i]++; placed = true; break; } } if (!placed) result[bins.length]++; }
    return result[0]; // return first bin count (array spill not yet supported)
  }
  case "GROWTH": return ERR.NA; // stub
  case "TREND": return ERR.NA; // stub
  case "FORECAST": case "FORECAST.LINEAR": {
    const x = toNum(e(args[0])); const ys = resolveRange(args[1].ref, cells).map(v => toNum(v)); const xs = resolveRange(args[2].ref, cells).map(v => toNum(v));
    const len = Math.min(ys.length, xs.length); if (len < 2) return ERR.NA;
    const mx = xs.slice(0, len).reduce((a, b) => a + b, 0) / len; const my = ys.slice(0, len).reduce((a, b) => a + b, 0) / len;
    let sxy = 0, sxx = 0; for (let i = 0; i < len; i++) { sxy += (xs[i] - mx) * (ys[i] - my); sxx += (xs[i] - mx) ** 2; }
    const slope = sxx === 0 ? 0 : sxy / sxx; return my + slope * (x - mx);
  }
  case "SLOPE": {
    const ys = resolveRange(args[0].ref, cells).map(v => toNum(v)); const xs = resolveRange(args[1].ref, cells).map(v => toNum(v));
    const len = Math.min(ys.length, xs.length); if (len < 2) return ERR.NA;
    const mx = xs.slice(0, len).reduce((a, b) => a + b, 0) / len; const my = ys.slice(0, len).reduce((a, b) => a + b, 0) / len;
    let sxy = 0, sxx = 0; for (let i = 0; i < len; i++) { sxy += (xs[i] - mx) * (ys[i] - my); sxx += (xs[i] - mx) ** 2; }
    return sxx === 0 ? ERR.DIV0 : sxy / sxx;
  }
  case "INTERCEPT": {
    const ys = resolveRange(args[0].ref, cells).map(v => toNum(v)); const xs = resolveRange(args[1].ref, cells).map(v => toNum(v));
    const len = Math.min(ys.length, xs.length); if (len < 2) return ERR.NA;
    const mx = xs.slice(0, len).reduce((a, b) => a + b, 0) / len; const my = ys.slice(0, len).reduce((a, b) => a + b, 0) / len;
    let sxy = 0, sxx = 0; for (let i = 0; i < len; i++) { sxy += (xs[i] - mx) * (ys[i] - my); sxx += (xs[i] - mx) ** 2; }
    return my - (sxx === 0 ? 0 : sxy / sxx) * mx;
  }
  case "RSQ": { const corr = evalFunc("CORREL", args, cells, ctx); return typeof corr === "number" ? corr * corr : corr; }

  // ═══ CONDITIONAL AGGREGATION ═══════════════════════════════════════
  case "SUMIF": case "COUNTIF": case "AVERAGEIF": {
    if (args[0].type !== "range") return ERR.VALUE;
    const cr = resolveRange(args[0].ref, cells); const crit = e(args[1]);
    const sr = args[2]?.type === "range" ? resolveRange(args[2].ref, cells) : cr;
    let sum = 0, cnt = 0;
    cr.forEach((v, i) => { if (matchCriteria(v, crit)) { sum += toNum(sr[i]) ?? 0; cnt++; } });
    if (name === "SUMIF") return sum; if (name === "COUNTIF") return cnt;
    return cnt === 0 ? ERR.DIV0 : sum / cnt;
  }
  case "SUMIFS": case "COUNTIFS": case "AVERAGEIFS": case "MAXIFS": case "MINIFS": {
    if (args[0].type !== "range") return ERR.VALUE;
    const sr = resolveRange(args[0].ref, cells);
    const pairs = []; for (let i = 1; i < args.length; i += 2) { if (args[i].type !== "range") return ERR.VALUE; pairs.push({ range: resolveRange(args[i].ref, cells), criteria: e(args[i + 1]) }); }
    let sum = 0, cnt = 0; const matched = [];
    sr.forEach((sv, idx) => {
      if (pairs.every(p => matchCriteria(p.range[idx], p.criteria))) { const nv = toNum(sv) ?? 0; sum += nv; cnt++; matched.push(nv); }
    });
    if (name === "SUMIFS") return sum; if (name === "COUNTIFS") return cnt;
    if (name === "AVERAGEIFS") return cnt === 0 ? ERR.DIV0 : sum / cnt;
    if (name === "MAXIFS") return matched.length ? Math.max(...matched) : 0;
    if (name === "MINIFS") return matched.length ? Math.min(...matched) : 0;
    return sum;
  }

  // ═══ LOOKUP & REFERENCE ═══════════════════════════════════════════════
  case "VLOOKUP": {
    const search = e(args[0]); if (args[1].type !== "range") return ERR.VALUE;
    const [st, en] = args[1].ref.split(":"); const s = parseCellRef(st), end = parseCellRef(en);
    if (!s || !end) return ERR.REF;
    const col = (toNum(e(args[2])) ?? 1) - 1;
    const approx = args[3] !== undefined ? toBool(e(args[3])) : true;
    if (!approx) {
      for (let r = s.row; r <= end.row; r++) { if (cells[cellKey(r, s.col)]?.computed == search) return cells[cellKey(r, s.col + col)]?.computed ?? ""; } // eslint-disable-line
      return ERR.NA;
    }
    // Approximate match: find largest value <= search
    let bestRow = -1, bestVal = -Infinity;
    for (let r = s.row; r <= end.row; r++) { const v = toNum(cells[cellKey(r, s.col)]?.computed); if (v !== null && v <= search && v > bestVal) { bestVal = v; bestRow = r; } }
    return bestRow >= 0 ? cells[cellKey(bestRow, s.col + col)]?.computed ?? "" : ERR.NA;
  }
  case "HLOOKUP": {
    const search = e(args[0]); if (args[1].type !== "range") return ERR.VALUE;
    const [st, en] = args[1].ref.split(":"); const s = parseCellRef(st), end = parseCellRef(en);
    if (!s || !end) return ERR.REF;
    const row = (toNum(e(args[2])) ?? 1) - 1;
    for (let c = s.col; c <= end.col; c++) { if (cells[cellKey(s.row, c)]?.computed == search) return cells[cellKey(s.row + row, c)]?.computed ?? ""; } // eslint-disable-line
    return ERR.NA;
  }
  case "XLOOKUP": {
    const search = e(args[0]);
    const lookupRange = args[1].type === "range" ? resolveRange(args[1].ref, cells) : [];
    const returnRange = args[2].type === "range" ? resolveRange(args[2].ref, cells) : [];
    const notFound = args[3] ? e(args[3]) : ERR.NA;
    const idx = lookupRange.findIndex(v => v == search); // eslint-disable-line
    return idx >= 0 ? (returnRange[idx] ?? "") : notFound;
  }
  case "INDEX": {
    if (args[0].type !== "range") return ERR.VALUE;
    const [st] = args[0].ref.split(":"); const s = parseCellRef(st); if (!s) return ERR.REF;
    const row = (toNum(e(args[1])) ?? 1) - 1; const col = args[2] ? (toNum(e(args[2])) ?? 1) - 1 : 0;
    return cells[cellKey(s.row + row, s.col + col)]?.computed ?? "";
  }
  case "MATCH": {
    const search = e(args[0]);
    const range = args[1].type === "range" ? resolveRange(args[1].ref, cells) : [];
    const type = args[2] !== undefined ? toNum(e(args[2])) ?? 1 : 1;
    if (type === 0) { const idx = range.findIndex(v => v == search); return idx === -1 ? ERR.NA : idx + 1; } // eslint-disable-line
    if (type === 1) { let best = -1; for (let i = 0; i < range.length; i++) { if ((toNum(range[i]) ?? -Infinity) <= search) best = i; } return best === -1 ? ERR.NA : best + 1; }
    if (type === -1) { let best = -1; for (let i = 0; i < range.length; i++) { if ((toNum(range[i]) ?? Infinity) >= search) best = i; } return best === -1 ? ERR.NA : best + 1; }
    return ERR.NA;
  }
  case "ROW": { if (args[0]?.type === "cell") { const p = parseCellRef(args[0].ref); return p ? p.row + 1 : ERR.REF; } return ERR.VALUE; }
  case "COLUMN": { if (args[0]?.type === "cell") { const p = parseCellRef(args[0].ref); return p ? p.col + 1 : ERR.REF; } return ERR.VALUE; }
  case "ROWS": { if (args[0]?.type === "range") { const [s, end] = args[0].ref.split(":"); const a = parseCellRef(s), b = parseCellRef(end); return a && b ? b.row - a.row + 1 : ERR.REF; } return 1; }
  case "COLUMNS": { if (args[0]?.type === "range") { const [s, end] = args[0].ref.split(":"); const a = parseCellRef(s), b = parseCellRef(end); return a && b ? b.col - a.col + 1 : ERR.REF; } return 1; }
  case "OFFSET": { // OFFSET(ref, rows, cols, [height], [width]) — simplified, returns single cell
    if (args[0]?.type !== "cell") return ERR.VALUE;
    const p = parseCellRef(args[0].ref); if (!p) return ERR.REF;
    const nr = p.row + (toNum(e(args[1])) ?? 0); const nc = p.col + (toNum(e(args[2])) ?? 0);
    return cells[cellKey(nr, nc)]?.computed ?? "";
  }
  case "INDIRECT": { const ref = toStr(e(args[0])).toUpperCase(); const p = parseCellRef(ref); if (!p) return ERR.REF; return cells[ref]?.computed ?? ""; }
  case "ADDRESS": {
    const r = toNum(e(args[0])) ?? 1; const c = toNum(e(args[1])) ?? 1;
    return "$" + colLabel(c - 1) + "$" + r;
  }
  case "TRANSPOSE": return args[0] ? e(args[0]) : ERR.VALUE; // simplified

  // ═══ FILTER / ARRAY ════════════════════════════════════════════════════
  case "FILTER": {
    if (args[0].type !== "range" || args[1].type !== "range") return ERR.VALUE;
    const data = resolveRange(args[0].ref, cells);
    const cond = resolveRange(args[1].ref, cells);
    const filtered = data.filter((_, i) => toBool(cond[i]));
    return filtered.length > 0 ? filtered[0] : (args[2] ? e(args[2]) : ERR.NA); // simplified: returns first match
  }
  case "SORT": {
    if (args[0].type !== "range") return ERR.VALUE;
    const data = resolveRange(args[0].ref, cells);
    const asc = args[2] ? toBool(e(args[2])) : true;
    const sorted = [...data].sort((a, b) => {
      const na = toNum(a), nb = toNum(b);
      if (na !== null && nb !== null) return asc ? na - nb : nb - na;
      return asc ? toStr(a).localeCompare(toStr(b)) : toStr(b).localeCompare(toStr(a));
    });
    return sorted[0] ?? ""; // simplified: returns first
  }
  case "UNIQUE": {
    if (args[0].type !== "range") return ERR.VALUE;
    const data = resolveRange(args[0].ref, cells);
    return [...new Set(data)][0] ?? ""; // simplified: returns first unique
  }
  case "FLATTEN": return args[0] ? e(args[0]) : ERR.VALUE; // stub

  // ═══ FINANCIAL ═════════════════════════════════════════════════════════
  case "PMT": {
    const r = toNum(e(args[0])) ?? 0; const n2 = toNum(e(args[1])) ?? 0; const pv = toNum(e(args[2])) ?? 0;
    const fv = args[3] ? toNum(e(args[3])) ?? 0 : 0; const type = args[4] ? toNum(e(args[4])) ?? 0 : 0;
    if (r === 0) return -(pv + fv) / n2;
    const p = Math.pow(1 + r, n2);
    return -(r * (pv * p + fv)) / (p - 1) / (1 + r * type);
  }
  case "FV": {
    const r = toNum(e(args[0])) ?? 0; const n2 = toNum(e(args[1])) ?? 0; const pmt = toNum(e(args[2])) ?? 0;
    const pv = args[3] ? toNum(e(args[3])) ?? 0 : 0; const type = args[4] ? toNum(e(args[4])) ?? 0 : 0;
    if (r === 0) return -(pv + pmt * n2);
    const p = Math.pow(1 + r, n2);
    return -(pv * p + pmt * (1 + r * type) * (p - 1) / r);
  }
  case "PV": {
    const r = toNum(e(args[0])) ?? 0; const n2 = toNum(e(args[1])) ?? 0; const pmt = toNum(e(args[2])) ?? 0;
    const fv = args[3] ? toNum(e(args[3])) ?? 0 : 0; const type = args[4] ? toNum(e(args[4])) ?? 0 : 0;
    if (r === 0) return -(fv + pmt * n2);
    const p = Math.pow(1 + r, n2);
    return -(fv + pmt * (1 + r * type) * (p - 1) / r) / p;
  }
  case "NPER": {
    const r = toNum(e(args[0])) ?? 0; const pmt = toNum(e(args[1])) ?? 0; const pv = toNum(e(args[2])) ?? 0;
    const fv = args[3] ? toNum(e(args[3])) ?? 0 : 0;
    if (r === 0) return -(pv + fv) / pmt;
    return Math.log((pmt - fv * r) / (pmt + pv * r)) / Math.log(1 + r);
  }
  case "RATE": { // Newton's method approximation
    const n2 = toNum(e(args[0])) ?? 0; const pmt = toNum(e(args[1])) ?? 0; const pv = toNum(e(args[2])) ?? 0;
    const fv = args[3] ? toNum(e(args[3])) ?? 0 : 0;
    let rate = 0.1;
    for (let i = 0; i < 100; i++) {
      const p = Math.pow(1 + rate, n2);
      const f = pv * p + pmt * (p - 1) / rate + fv;
      const df = pv * n2 * Math.pow(1 + rate, n2 - 1) + pmt * (n2 * Math.pow(1 + rate, n2 - 1) * rate - (p - 1)) / (rate * rate);
      const newRate = rate - f / df;
      if (Math.abs(newRate - rate) < 1e-10) return newRate;
      rate = newRate;
    }
    return rate;
  }
  case "IPMT": {
    const r = toNum(e(args[0])) ?? 0; const per = toNum(e(args[1])) ?? 0; const n2 = toNum(e(args[2])) ?? 0; const pv = toNum(e(args[3])) ?? 0;
    const pmt2 = evalFunc("PMT", [args[0], args[2], args[3], args[4] || { type: "literal", value: 0 }], cells, ctx);
    const fvPrev = evalFunc("FV", [args[0], { type: "literal", value: per - 1 }, { type: "literal", value: pmt2 }, args[3]], cells, ctx);
    return fvPrev * r;
  }
  case "PPMT": {
    const pmt2 = evalFunc("PMT", args, cells, ctx);
    const ipmt2 = evalFunc("IPMT", args, cells, ctx);
    return typeof pmt2 === "number" && typeof ipmt2 === "number" ? pmt2 - ipmt2 : ERR.VALUE;
  }
  case "NPV": {
    const r = toNum(e(args[0])) ?? 0;
    const cfs = getAllVals(args.slice(1), cells, ctx);
    return cfs.reduce((npv, cf, i) => npv + (toNum(cf) ?? 0) / Math.pow(1 + r, i + 1), 0);
  }
  case "IRR": {
    const cfs = args[0].type === "range" ? resolveRange(args[0].ref, cells).map(v => toNum(v) ?? 0) : [];
    let rate = args[1] ? toNum(e(args[1])) ?? 0.1 : 0.1;
    for (let i = 0; i < 100; i++) {
      let npv = 0, dnpv = 0;
      cfs.forEach((cf, t) => { npv += cf / Math.pow(1 + rate, t); dnpv -= t * cf / Math.pow(1 + rate, t + 1); });
      if (Math.abs(npv) < 1e-10) return rate;
      rate -= npv / dnpv;
    }
    return rate;
  }
  case "MIRR": {
    const cfs = args[0].type === "range" ? resolveRange(args[0].ref, cells).map(v => toNum(v) ?? 0) : [];
    const fRate = toNum(e(args[1])) ?? 0; const rRate = toNum(e(args[2])) ?? 0; const n2 = cfs.length;
    let npvNeg = 0, fvPos = 0;
    cfs.forEach((cf, t) => { if (cf < 0) npvNeg += cf / Math.pow(1 + fRate, t); else fvPos += cf * Math.pow(1 + rRate, n2 - 1 - t); });
    return Math.pow(-fvPos / npvNeg, 1 / (n2 - 1)) - 1;
  }
  case "SLN": { const cost = toNum(e(args[0])) ?? 0; const salv = toNum(e(args[1])) ?? 0; const life = toNum(e(args[2])) ?? 1; return (cost - salv) / life; }
  case "SYD": {
    const cost = toNum(e(args[0])) ?? 0; const salv = toNum(e(args[1])) ?? 0; const life = toNum(e(args[2])) ?? 1; const per = toNum(e(args[3])) ?? 1;
    return (cost - salv) * (life - per + 1) * 2 / (life * (life + 1));
  }
  case "DB": {
    const cost = toNum(e(args[0])) ?? 0; const salv = toNum(e(args[1])) ?? 0; const life = toNum(e(args[2])) ?? 1;
    const per = toNum(e(args[3])) ?? 1; const month = args[4] ? toNum(e(args[4])) ?? 12 : 12;
    const rate = Math.round((1 - Math.pow(salv / cost, 1 / life)) * 1000) / 1000;
    let val = cost; let dep = 0;
    for (let i = 1; i <= per; i++) {
      if (i === 1) dep = cost * rate * month / 12;
      else dep = val * rate;
      val -= dep;
    }
    return dep;
  }
  case "DDB": {
    const cost = toNum(e(args[0])) ?? 0; const salv = toNum(e(args[1])) ?? 0; const life = toNum(e(args[2])) ?? 1;
    const per = toNum(e(args[3])) ?? 1; const factor = args[4] ? toNum(e(args[4])) ?? 2 : 2;
    let val = cost;
    for (let i = 1; i < per; i++) val -= Math.min(val * factor / life, val - salv);
    return Math.min(val * factor / life, val - salv);
  }
  case "EFFECT": { const nom = toNum(e(args[0])) ?? 0; const npery = toNum(e(args[1])) ?? 1; return Math.pow(1 + nom / npery, npery) - 1; }
  case "NOMINAL": { const eff = toNum(e(args[0])) ?? 0; const npery = toNum(e(args[1])) ?? 1; return npery * (Math.pow(1 + eff, 1 / npery) - 1); }
  case "FVSCHEDULE": { let pv = toNum(e(args[0])) ?? 0; const rates = args[1].type === "range" ? resolveRange(args[1].ref, cells) : [e(args[1])]; rates.forEach(r => { pv *= 1 + (toNum(r) ?? 0); }); return pv; }
  case "PDURATION": { const r = toNum(e(args[0])) ?? 0; const pv = toNum(e(args[1])) ?? 0; const fv = toNum(e(args[2])) ?? 0; return r === 0 ? ERR.NUM : (Math.log(fv) - Math.log(pv)) / Math.log(1 + r); }
  case "DOLLARDE": { const frac = toNum(e(args[0])) ?? 0; const unit = toNum(e(args[1])) ?? 1; const intPart = Math.floor(frac); return intPart + (frac - intPart) / unit * Math.pow(10, Math.ceil(Math.log10(unit))); }
  case "DOLLARFR": { const dec = toNum(e(args[0])) ?? 0; const unit = toNum(e(args[1])) ?? 1; const intPart = Math.floor(dec); return intPart + (dec - intPart) * unit / Math.pow(10, Math.ceil(Math.log10(unit))); }

  // ═══ ENGINEERING ═══════════════════════════════════════════════════════
  case "BIN2DEC": return parseInt(toStr(e(args[0])), 2);
  case "BIN2HEX": return parseInt(toStr(e(args[0])), 2).toString(16).toUpperCase();
  case "BIN2OCT": return parseInt(toStr(e(args[0])), 2).toString(8);
  case "DEC2BIN": return (toNum(e(args[0])) ?? 0).toString(2);
  case "DEC2HEX": return (toNum(e(args[0])) ?? 0).toString(16).toUpperCase();
  case "DEC2OCT": return (toNum(e(args[0])) ?? 0).toString(8);
  case "HEX2BIN": return parseInt(toStr(e(args[0])), 16).toString(2);
  case "HEX2DEC": return parseInt(toStr(e(args[0])), 16);
  case "HEX2OCT": return parseInt(toStr(e(args[0])), 16).toString(8);
  case "OCT2BIN": return parseInt(toStr(e(args[0])), 8).toString(2);
  case "OCT2DEC": return parseInt(toStr(e(args[0])), 8);
  case "OCT2HEX": return parseInt(toStr(e(args[0])), 8).toString(16).toUpperCase();
  case "DELTA": return (toNum(e(args[0])) ?? 0) === (toNum(e(args[1])) ?? 0) ? 1 : 0;
  case "GESTEP": return (toNum(e(args[0])) ?? 0) >= (toNum(e(args[1])) ?? 0) ? 1 : 0;
  case "BITAND": return (toNum(e(args[0])) ?? 0) & (toNum(e(args[1])) ?? 0);
  case "BITOR": return (toNum(e(args[0])) ?? 0) | (toNum(e(args[1])) ?? 0);
  case "BITXOR": return (toNum(e(args[0])) ?? 0) ^ (toNum(e(args[1])) ?? 0);
  case "BITLSHIFT": return (toNum(e(args[0])) ?? 0) << (toNum(e(args[1])) ?? 0);
  case "BITRSHIFT": return (toNum(e(args[0])) ?? 0) >> (toNum(e(args[1])) ?? 0);
  case "COMPLEX": { const r = toNum(e(args[0])) ?? 0; const im = toNum(e(args[1])) ?? 0; const sfx = args[2] ? toStr(e(args[2])) : "i"; return (r !== 0 ? String(r) : "") + (im !== 0 ? (im > 0 && r !== 0 ? "+" : "") + im + sfx : (r === 0 ? "0" : "")); }
  case "IMREAL": { const s = toStr(e(args[0])); const m = s.match(/^([+-]?\d*\.?\d+)/); return m ? Number(m[1]) : 0; }
  case "IMAGINARY": { const s = toStr(e(args[0])); const m = s.match(/([+-]?\d*\.?\d+)[ij]$/); return m ? Number(m[1]) : 0; }
  case "IMABS": { const r = evalFunc("IMREAL", args, cells, ctx); const im = evalFunc("IMAGINARY", args, cells, ctx); return Math.sqrt(r * r + im * im); }
  case "ERF": case "ERF.PRECISE": return erf(toNum(e(args[0])) ?? 0);

  // ═══ DATABASE ══════════════════════════════════════════════════════════
  // Simplified stubs — these require structured table format
  case "DSUM": case "DAVERAGE": case "DCOUNT": case "DCOUNTA": case "DMAX": case "DMIN":
  case "DGET": case "DPRODUCT": case "DSTDEV": case "DSTDEVP": case "DVAR": case "DVARP":
    return ERR.NA; // Database functions require complex table parsing — stubbed for now

  // ═══ CRM-SPECIFIC ═════════════════════════════════════════════════════
  case "CF.CONTACT": {
    const lk = toStr(e(args[0])); const fld = toStr(e(args[1]));
    if (!lk || !fld || !ctx.contacts) return ERR.NA;
    const ct = ctx.contacts.find(c => c.email === lk || c.id === lk);
    if (!ct) return ERR.NA;
    return ct[fld] ?? ct[fld.toLowerCase()] ?? "";
  }
  case "CF.OWNER": {
    const lk = toStr(e(args[0]));
    if (!lk || !ctx.contacts) return ERR.NA;
    const ct = ctx.contacts.find(c => c.id === lk || c.email === lk);
    return ct?.ownerName || ct?.owner || "";
  }
  case "CF.DEAL": {
    const lk = toStr(e(args[0])); const fld = toStr(e(args[1]));
    if (!lk || !fld || !ctx.deals) return ERR.NA;
    const dl = ctx.deals.find(d => d.id === lk || d.name === lk);
    return dl ? (dl[fld] ?? dl[fld.toLowerCase()] ?? "") : ERR.NA;
  }
  case "CF.SCORE": {
    const lk = toStr(e(args[0]));
    if (!lk || !ctx.contacts) return ERR.NA;
    const ct = ctx.contacts.find(c => c.id === lk || c.email === lk);
    return ct?.score ?? 0;
  }

  // ═══ FALLBACK ═════════════════════════════════════════════════════════
  default: return ERR.NAME;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  MATH HELPERS (Error function for statistical distributions)
// ═══════════════════════════════════════════════════════════════════════════
function erf(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429;
  const p = 0.3275911;
  const sign = x >= 0 ? 1 : -1; x = Math.abs(x);
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}

function erfinv(x) {
  // Rational approximation for inverse error function
  const a = 0.147; const ln = Math.log(1 - x * x);
  const s = Math.sign(x);
  const t1 = 2 / (Math.PI * a) + ln / 2;
  const t2 = ln / a;
  return s * Math.sqrt(Math.sqrt(t1 * t1 - t2) - t1);
}

// ═══════════════════════════════════════════════════════════════════════════
//  CELL EVALUATION ENGINE
// ═══════════════════════════════════════════════════════════════════════════
export function evaluateAllCells(cellData, ctx = {}) {
  const cells = {}; const deps = {};

  // Phase 1: Parse all formulas
  for (const [key, val] of Object.entries(cellData)) {
    const raw = val?.raw ?? val ?? "";
    cells[key] = { raw, computed: undefined, formula: null, error: null };
    if (typeof raw === "string" && raw.startsWith("=")) {
      try { const ast = parse(tokenize(raw.slice(1))); cells[key].formula = ast; deps[key] = extractDeps(ast); }
      catch { cells[key].error = ERR.VALUE; cells[key].computed = ERR.VALUE; }
    } else {
      const num = Number(raw);
      cells[key].computed = raw === "" ? "" : (!isNaN(num) && raw !== "" && raw !== true && raw !== false ? num : raw);
    }
  }

  // Phase 2: Topological sort
  const order = []; const visiting = new Set(); const visited = new Set();
  function visit(key) {
    if (visited.has(key)) return true;
    if (visiting.has(key)) { cells[key].computed = ERR.CIRC; cells[key].error = ERR.CIRC; return false; }
    visiting.add(key);
    for (const dep of (deps[key] || [])) { if (!visit(dep)) { cells[key].computed = ERR.CIRC; cells[key].error = ERR.CIRC; return false; } }
    visiting.delete(key); visited.add(key); order.push(key); return true;
  }
  for (const key of Object.keys(cells)) visit(key);

  // Phase 3: Evaluate in order
  for (const key of order) {
    if (cells[key].computed !== undefined) continue;
    if (cells[key].formula) {
      try {
        cells[key].computed = evaluate(cells[key].formula, cells, { ...ctx, currentCell: key, visited: new Set([key]) });
        if (isErr(cells[key].computed)) cells[key].error = cells[key].computed;
      } catch { cells[key].computed = ERR.VALUE; cells[key].error = ERR.VALUE; }
    }
  }

  return cells;
}

// ═══════════════════════════════════════════════════════════════════════════
//  FORMAT DISPLAY VALUE
// ═══════════════════════════════════════════════════════════════════════════
export function formatCell(value, format) {
  if (value === "" || value === null || value === undefined) return "";
  if (isErr(value)) return value;
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  if (format === "currency" && typeof value === "number") return "$" + value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (format === "percent" && typeof value === "number") return (value * 100).toFixed(1) + "%";
  if (format === "date" && typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value); return isNaN(d) ? value : (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();
  }
  if (typeof value === "number") return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  return String(value);
}

// ═══════════════════════════════════════════════════════════════════════════
//  FUNCTION LIST (for autocomplete)
// ═══════════════════════════════════════════════════════════════════════════
export const FUNCTION_LIST = [
  // Math
  "SUM","AVERAGE","AVERAGEA","COUNT","COUNTA","COUNTBLANK","MIN","MINA","MAX","MAXA",
  "ROUND","ROUNDUP","ROUNDDOWN","MROUND","ABS","INT","TRUNC","MOD","POWER","SQRT","SQRTPI",
  "PRODUCT","SUMPRODUCT","SUMSQ","CEILING","CEILING.MATH","FLOOR","FLOOR.MATH","SIGN",
  "RAND","RANDBETWEEN","LOG","LOG10","LN","EXP","PI","SIN","COS","TAN","ASIN","ACOS","ATAN","ATAN2",
  "SINH","COSH","TANH","ASINH","ACOSH","ATANH","DEGREES","RADIANS","EVEN","ODD",
  "FACT","FACTDOUBLE","COMBIN","COMBINA","GCD","LCM","QUOTIENT","ISODD","ISEVEN",
  "SERIESSUM","SUBTOTAL","BASE","DECIMAL",
  // Text
  "CONCATENATE","CONCAT","TEXTJOIN","LEFT","RIGHT","MID","LEN","UPPER","LOWER","PROPER",
  "TRIM","CLEAN","SUBSTITUTE","REPLACE","FIND","SEARCH","REPT","VALUE","T","N","FIXED","DOLLAR",
  "TEXT","CHAR","CODE","EXACT","REGEXMATCH","REGEXEXTRACT","REGEXREPLACE","SPLIT","JOIN",
  "ARRAYFORMULA","HYPERLINK","ROMAN","ARABIC",
  // Logical
  "IF","IFS","AND","OR","XOR","NOT","IFERROR","IFNA","SWITCH","CHOOSE","TRUE","FALSE",
  // Info
  "ISBLANK","ISNUMBER","ISTEXT","ISLOGICAL","ISERROR","ISERR","ISNA","ISREF","ISFORMULA",
  "TYPE","ERROR.TYPE","NA",
  // Date
  "TODAY","NOW","DATE","DATEVALUE","TIME","TIMEVALUE","YEAR","MONTH","DAY","WEEKDAY","WEEKNUM",
  "ISOWEEKNUM","HOUR","MINUTE","SECOND","DATEDIF","DAYS","DAYS360","EDATE","EOMONTH",
  "NETWORKDAYS","NETWORKDAYS.INTL","WORKDAY","WORKDAY.INTL","YEARFRAC",
  // Statistical
  "MEDIAN","MODE","MODE.SNGL","STDEV","STDEV.S","STDEVP","STDEV.P","VAR","VAR.S","VARP","VAR.P",
  "RANK","RANK.EQ","RANK.AVG","LARGE","SMALL","PERCENTILE","PERCENTILE.INC","QUARTILE","QUARTILE.INC",
  "CORREL","COVARIANCE.P","COVAR","NORM.DIST","NORMDIST","NORM.S.DIST","NORMSDIST",
  "NORM.INV","NORMINV","NORM.S.INV","NORMSINV","AVERAGE.WEIGHTED","AVERAGEWEIGHTED",
  "FREQUENCY","FORECAST","FORECAST.LINEAR","SLOPE","INTERCEPT","RSQ",
  // Conditional
  "SUMIF","COUNTIF","AVERAGEIF","SUMIFS","COUNTIFS","AVERAGEIFS","MAXIFS","MINIFS",
  // Lookup
  "VLOOKUP","HLOOKUP","XLOOKUP","INDEX","MATCH","ROW","COLUMN","ROWS","COLUMNS",
  "OFFSET","INDIRECT","ADDRESS","TRANSPOSE",
  // Filter/Array
  "FILTER","SORT","UNIQUE","FLATTEN",
  // Financial
  "PMT","FV","PV","NPER","RATE","IPMT","PPMT","NPV","IRR","MIRR","SLN","SYD","DB","DDB",
  "EFFECT","NOMINAL","FVSCHEDULE","PDURATION","DOLLARDE","DOLLARFR",
  // Engineering
  "BIN2DEC","BIN2HEX","BIN2OCT","DEC2BIN","DEC2HEX","DEC2OCT","HEX2BIN","HEX2DEC","HEX2OCT",
  "OCT2BIN","OCT2DEC","OCT2HEX","DELTA","GESTEP","BITAND","BITOR","BITXOR","BITLSHIFT","BITRSHIFT",
  "COMPLEX","IMREAL","IMAGINARY","IMABS","ERF","ERF.PRECISE",
  // Database (stubs)
  "DSUM","DAVERAGE","DCOUNT","DCOUNTA","DMAX","DMIN","DGET","DPRODUCT","DSTDEV","DSTDEVP","DVAR","DVARP",
  // CRM
  "CF.CONTACT","CF.OWNER","CF.DEAL","CF.SCORE",
].sort();
